import React from "react";
import toast from "react-hot-toast";
import {Controller, FormProvider, useForm} from "react-hook-form";
import {useSelector} from "react-redux";
import {RootState} from "@/store";
import PricingForm from "@/pages/Venues/Edit/PricingForm";
import DateTimeInput from "@/components/common/DateTimeInput";
import Button from "@/components/common/Button";
import Modal from "@/components/common/Modal";
import {dayType, priceType} from "@/pages/Venues/Edit/contants";
import {
  getDateTimeInputString,
  transformDateTimeInputStringToTimestamp
} from "@/utils";
import {hourRule, validatePriceDetail} from "@/utils/validation";

interface PricingClassFormState {
  name: string,
  REGULAR: {
    WEEKDAYS: PriceDetail[],
    WEEKEND: PriceDetail[],
    HOLIDAY: PriceDetail[],
  }
  EVENT: {
    WEEKDAYS: PriceDetail[],
    WEEKEND: PriceDetail[]
  }
  appliedDate: number
}

interface PricingModalProps {
  closeModal: Function
  onSubmit: (price: Price) => void
}

const getPopulateData = (priceDataType, dayDataType) => ({
  type: priceDataType,
  day_type: dayDataType,
  start_time: hourRule.start,
  end_time: hourRule.end,
  price: null
})

const emptyStateValues: PricingClassFormState = {
  name: '',
  REGULAR: {
    WEEKDAYS: [getPopulateData(priceType.regular, dayType.weekdays)],
    WEEKEND: [getPopulateData(priceType.regular, dayType.weekend)],
    HOLIDAY: [getPopulateData(priceType.regular, dayType.holiday)],
  },
  EVENT: {
    WEEKDAYS: [getPopulateData(priceType.event, dayType.weekdays)],
    WEEKEND: [getPopulateData(priceType.event, dayType.weekend)]
  },
  appliedDate: Date.now()
}

const getClassByFilter = (priceDetails: PriceDetail[], type: string, daysType: string) =>
  priceDetails.filter(p => p.day_type === daysType && p.type === type)

const validatePrice = (price: Price) => {
    let errorMessage = null
    if (!price.name) {
        errorMessage = 'Price name can not be empty'
    } else if (!price.details.every(validatePriceDetail)) {
        errorMessage = `Price details are wrong. Make sure hour is between ${hourRule.start}-${hourRule.end} and price is filled`
    }

    if (errorMessage) {
      toast.error(errorMessage)
    }
    return !errorMessage
}

const PricingModal: React.FC<PricingModalProps> =
  ({
     closeModal,
     onSubmit
   }) => {
    const {currentPrice} = useSelector((state: RootState) => state.venueDetail)
    const priceDetails = currentPrice?.details ?? null
    const defaultValues: PricingClassFormState =
      currentPrice && priceDetails ? {
        name: currentPrice.name,
        REGULAR: {
          WEEKDAYS: getClassByFilter(priceDetails, priceType.regular, dayType.weekdays),
          WEEKEND: getClassByFilter(priceDetails, priceType.regular, dayType.weekend),
          HOLIDAY: getClassByFilter(priceDetails, priceType.regular, dayType.holiday),
        },
        EVENT: {
          WEEKDAYS: getClassByFilter(priceDetails, priceType.event, dayType.weekdays),
          WEEKEND: getClassByFilter(priceDetails, priceType.event, dayType.weekend)
        },
        appliedDate: currentPrice.applied_date,
      } : emptyStateValues
    const pricingMethods = useForm<PricingClassFormState>({
      defaultValues
    })
    
    const cleanPriceDetail = (dirtyPriceDetail): PriceDetail => ({
      ...dirtyPriceDetail,
      start_time: parseInt(dirtyPriceDetail.start_time, 10),
      end_time: parseInt(dirtyPriceDetail.end_time, 10),
      price: parseInt(dirtyPriceDetail.price, 10)
    })
    
    const handleClickSubmit = () => {
      const newPriceFormValues = pricingMethods.getValues()
      const details: PriceDetail[] = [
        ...(newPriceFormValues.REGULAR.WEEKDAYS.map(cleanPriceDetail)),
        ...(newPriceFormValues.REGULAR.WEEKEND.map(cleanPriceDetail)),
        ...(newPriceFormValues.REGULAR.HOLIDAY.map(cleanPriceDetail)),
        ...(newPriceFormValues.EVENT.WEEKDAYS.map(cleanPriceDetail)),
        ...(newPriceFormValues.EVENT.WEEKEND.map(cleanPriceDetail))
      ]
      const price: Price = {
        ...currentPrice,
        details,
        name: newPriceFormValues.name,
        applied_date: newPriceFormValues.appliedDate,
      }
      if (!validatePrice(price)) {
        return
      }
      onSubmit(price)
    }
    
    return (
      <FormProvider {...pricingMethods}>
        <Modal
          show
          header={{title: 'Add/Edit Price Class'}}
          body={{children: <PricingForm/>}}
          footer={{
            children: (
              <div className="flex w-full justify-between items-center p-5">
                <div className="flex justify-start items-center gap-3">
                  <div className="text-jsm font-medium">Start Date</div>
                  <Controller render={({field}) => (
                    <DateTimeInput
                      onChange={(event) => {
                        const updateDate = transformDateTimeInputStringToTimestamp(event.target.value)
                        field.onChange(updateDate)
                      }}
                      value={getDateTimeInputString(new Date(field.value))}/>)}
                              name='appliedDate'/>
                </div>
                <Button
                  onClick={handleClickSubmit}
                  type="submit"
                  variant="primary"
                  className="whitespace-nowrap flex gap-2 px-12 py-2 justify-center items-center text-jsm font-medium"
                >
                  Submit
                </Button>
              </div>)
          }}
          onClick={() => closeModal}
          rootId="venue-detail-price-form"/>
      </FormProvider>
    );
  };

export default PricingModal;